// Подсказка первого раза: облачко у элемента, один раз в жизни.
//
// ## Почему облачко, а не затемнение всего экрана
//
// На телефоне подсказка затемняет экран и вырезает окно вокруг элемента: там мало места, палец
// толстый, и без выреза непонятно, о чём речь. В кабинете это было бы грубо — человек сидит за
// монитором, у него есть курсор и есть контекст, а затемнение в полтора мегапикселя посреди
// работы читается как «программа зависла». Облачко у самого элемента говорит то же самое и не
// отбирает экран.
//
// ## Выбирает КООРДИНАТОР, а не гонка таймеров
//
// Элементов с подсказками на экране бывает несколько, и появляются они в порядке отрисовки: полоса
// разделов раньше содержимого раздела. Если бы место занимала первая нарисовавшаяся, человек
// год видел бы подсказки про полосу и ни одной про свою работу.
//
// Поэтому каждая подсказка не показывает себя, а ОБЪЯВЛЯЕТСЯ. Координатор ждёт, пока экран
// доедет, и выбирает первую непоказанную по порядку из `уроки.ts` — там он и задан смыслом:
// сначала про работу, потом про инструменты.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { RiLightbulbLine } from '@remixicon/react';

import { useAuthStore } from '@/shared/auth/store';
import { useСреда } from '@/shared/среда';
import * as Button from '@/shared/ui/button';
import * as Popover from '@/shared/ui/popover';

import { запомнитьПоказ, подсказкиРазрешены, ужеВидел } from './память';
import { ТЕКСТЫ, УРОКИ, type Урок } from './уроки';

/** Пауза, пока экран доедет. Облачко посреди загрузки читается как дефект отрисовки. */
const ЖДЁМ_ЭКРАН_МС = 700;

interface Координатор {
  выбранная: Урок | null;
  объявить(урок: Урок): () => void;
  закрыть(): void;
}

const КОНТЕКСТ = createContext<Координатор | null>(null);

/** Один на защищённую область: пропускает одну подсказку и молчит про остальные. */
export function ПодсказкиНаЭкране({ children }: { children: ReactNode }) {
  const хозяин = useAuthStore((state) => state.user?.id ?? null);
  const наЭкране = useRef<Set<Урок>>(new Set());
  const [выбранная, setВыбранная] = useState<Урок | null>(null);
  // Одна подсказка за заход, даже если непоказанных несколько: вторая подряд читается уже не
  // как обучение. Признак снимается сменой человека, а не сменой раздела.
  const хватит = useRef(false);
  const [пересчёт, setПересчёт] = useState(0);

  useEffect(() => {
    хватит.current = false;
    setВыбранная(null);
  }, [хозяин]);

  useEffect(() => {
    if (хозяин === null || хватит.current || выбранная !== null || !подсказкиРазрешены()) {
      return;
    }
    const таймер = window.setTimeout(() => {
      const следующая = УРОКИ.find((урок) => наЭкране.current.has(урок) && !ужеВидел(хозяин, урок));
      if (следующая === undefined) {
        return;
      }
      хватит.current = true;
      // ⚠️ Показ засчитывается ЗДЕСЬ, перед отрисовкой, а не по нажатию «Понятно»: уйти с
      // экрана можно, её не тронув, и любой учёт «по закрытию» этот случай теряет.
      запомнитьПоказ(хозяин, следующая);
      setВыбранная(следующая);
    }, ЖДЁМ_ЭКРАН_МС);
    return () => window.clearTimeout(таймер);
  }, [хозяин, пересчёт, выбранная]);

  const координатор = useMemo<Координатор>(
    () => ({
      выбранная,
      объявить: (урок) => {
        наЭкране.current.add(урок);
        setПересчёт((н) => н + 1);
        return () => {
          наЭкране.current.delete(урок);
        };
      },
      закрыть: () => setВыбранная(null),
    }),
    [выбранная],
  );

  return <КОНТЕКСТ.Provider value={координатор}>{children}</КОНТЕКСТ.Provider>;
}

/**
 * Облачко у элемента, на который указывает урок.
 *
 * Элемент передаётся ССЫЛКОЙ, а не оборачивается. Обёртка казалась проще и оказалась ловушкой:
 * `display: contents` не даёт браузеру коробки, и облачко уезжало в левый верхний угол экрана
 * поверх полосы разделов; а обычная обёртка ломает раскладку там, где элемент — гибкий
 * (`flex-1`) или единственный ребёнок сетки. Ссылка не меняет разметку вовсе.
 */
export function Подсказка({
  урок,
  якорь,
  когда = true,
}: {
  урок: Урок;
  якорь: RefObject<HTMLElement | null>;
  /**
   * Показывать ли подсказку СЕЙЧАС. Ложь — урок не объявляется вовсе и остаётся непоказанным.
   *
   * Нужна затем, что один и тот же экран у разных ролей несёт разный смысл. Пульт подотчёта
   * полевому руководителю показывает ровно одну строку — его самого; подсказка «сверху тот, у
   * кого висит дольше» на списке из одной строки не объясняет ничего и тратит единственный
   * показ, который у урока есть за всё время.
   *
   * Урок при этом НЕ сгорает: он покажется тогда, когда станет осмысленным.
   */
  когда?: boolean;
}) {
  const координатор = useContext(КОНТЕКСТ);

  useEffect(() => {
    if (!когда) return;
    return координатор?.объявить(урок);
  }, [координатор, урок, когда]);

  const { клавиатура } = useСреда();
  const открыта = когда && координатор?.выбранная === урок;
  const текст = ТЕКСТЫ[урок];
  const шаг = !клавиатура && текст.безКлавиатуры !== undefined ? текст.безКлавиатуры : текст.текст;

  return (
    <Popover.Root open={открыта} onOpenChange={(о) => !о && координатор?.закрыть()}>
      <Popover.Anchor virtualRef={якорь} />
      <Popover.Content align='start' side='bottom' className='w-80 p-4'>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-2'>
            <RiLightbulbLine className='size-4 shrink-0 text-text-soft-400' />
            <span className='text-label-sm text-text-strong-950'>{текст.заголовок}</span>
          </div>
          <p className='text-paragraph-sm text-text-sub-600'>{шаг}</p>
          <div className='flex justify-end pt-1'>
            <Button.Root
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => координатор?.закрыть()}
            >
              Понятно
            </Button.Root>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
